/* eslint-disable @typescript-eslint/no-use-before-define */
import { escapeHtml, renderActivityLogTextMarkdownHtml } from './activityLogLinks';
import {
  ActivityLogMarkdownBlock,
  ActivityLogMarkdownTableAlign,
  parseActivityLogMarkdownBlocksWithInlineRenderer,
} from './activityLogMarkdownBlocks';
import {
  ACTIVITY_LOG_AGENT_FALLBACK_LABEL,
  ParsedActivityLogMention,
  findActivityLogMentionMatches,
  getActivityLogMentionDisplayLabel,
  serializeActivityLogMentionToken,
} from './activityLogMentionTokens';

type ActivityLogJsonContent = {
  type?: string;
  text?: string;
  attrs?: Record<string, unknown> | null;
  content?: ActivityLogJsonContent[];
};

const EMPTY_ACTIVITY_LOG_CONTENT = {
  markdown: '',
  plaintext: '',
};

export { ACTIVITY_LOG_AGENT_FALLBACK_LABEL };
export type { ActivityLogMarkdownBlock, ActivityLogMarkdownTableAlign };

function renderMentionHtml(parsed: ParsedActivityLogMention, aiAgentLabel: string) {
  const isAgent = parsed.type === 'agent';
  const entityId = isAgent ? 'ai-agent' : (parsed.id ?? '');
  const displayLabel = getActivityLogMentionDisplayLabel(parsed, aiAgentLabel);
  const mentionType = parsed.type;

  return `<span data-type="mention" data-id="${escapeHtml(entityId)}" data-label="${escapeHtml(
    displayLabel
  )}" data-mention-type="${mentionType}" class="activity-log-mention activity-log-mention--${mentionType}">@${escapeHtml(
    displayLabel
  )}</span>`;
}

function serializeMention(attrs: Record<string, unknown> | null | undefined, aiAgentLabel: string) {
  const id = typeof attrs?.id === 'string' ? attrs.id : '';
  const rawLabel = typeof attrs?.label === 'string' ? attrs.label : '';

  if (id === 'ai-agent') {
    const parsed: ParsedActivityLogMention = {
      type: 'agent',
      ...(rawLabel ? { label: rawLabel } : {}),
    };

    return {
      markdown: serializeActivityLogMentionToken(parsed),
      plaintext: `@${getActivityLogMentionDisplayLabel(parsed, aiAgentLabel)}`,
    };
  }

  if (!id) {
    return EMPTY_ACTIVITY_LOG_CONTENT;
  }

  const parsed: ParsedActivityLogMention = {
    type: 'employee',
    id,
    ...(rawLabel ? { label: rawLabel } : {}),
  };

  return {
    markdown: serializeActivityLogMentionToken(parsed),
    plaintext: `@${getActivityLogMentionDisplayLabel(parsed, aiAgentLabel)}`,
  };
}

function serializeInlineNodes(nodes: ActivityLogJsonContent[], aiAgentLabel: string) {
  return nodes.reduce(
    (acc, node) => {
      const serialized = serializeNode(node, aiAgentLabel);
      return {
        markdown: acc.markdown + serialized.markdown,
        plaintext: acc.plaintext + serialized.plaintext,
      };
    },
    { ...EMPTY_ACTIVITY_LOG_CONTENT }
  );
}

function serializeBlockNodes(nodes: ActivityLogJsonContent[], aiAgentLabel: string) {
  return nodes.reduce(
    (acc, node, index) => {
      const serialized = serializeNode(node, aiAgentLabel);
      const separator = index > 0 && serialized.markdown ? '\n\n' : '';
      return {
        markdown: acc.markdown + separator + serialized.markdown,
        plaintext: acc.plaintext + separator + serialized.plaintext,
      };
    },
    { ...EMPTY_ACTIVITY_LOG_CONTENT }
  );
}

function serializeNode(node: ActivityLogJsonContent, aiAgentLabel: string): { markdown: string; plaintext: string } {
  switch (node.type) {
    case 'doc':
      return serializeBlockNodes(node.content ?? [], aiAgentLabel);
    case 'paragraph':
      return serializeInlineNodes(node.content ?? [], aiAgentLabel);
    case 'text':
      return {
        markdown: node.text ?? '',
        plaintext: node.text ?? '',
      };
    case 'mention':
      return serializeMention(node.attrs, aiAgentLabel);
    case 'hardBreak':
      return {
        markdown: '\n',
        plaintext: '\n',
      };
    default:
      if (node.content?.length) {
        return serializeInlineNodes(node.content, aiAgentLabel);
      }
      return EMPTY_ACTIVITY_LOG_CONTENT;
  }
}

function renderTextAndMentionsHtml(text: string, aiAgentLabel: string) {
  let html = '';
  let lastIndex = 0;

  for (const match of findActivityLogMentionMatches(text)) {
    html += renderActivityLogTextMarkdownHtml(text.slice(lastIndex, match.index));
    html += renderMentionHtml(match.parsed, aiAgentLabel);
    lastIndex = match.index + match.length;
  }

  html += renderActivityLogTextMarkdownHtml(text.slice(lastIndex));
  return html;
}

function renderInlineHtml(text: string, aiAgentLabel: string) {
  return renderTextAndMentionsHtml(text, aiAgentLabel);
}

export function serializeActivityLogContent(doc: ActivityLogJsonContent, aiAgentLabel: string) {
  return serializeNode(doc, aiAgentLabel);
}

export function renderActivityLogMentionMarkdown(markdown: string, aiAgentLabel: string) {
  const matches = findActivityLogMentionMatches(markdown);
  if (matches.length === 0) {
    return markdown;
  }

  let result = '';
  let lastIndex = 0;

  for (const match of matches) {
    result += markdown.slice(lastIndex, match.index);
    result += `@${getActivityLogMentionDisplayLabel(match.parsed, aiAgentLabel)}`;
    lastIndex = match.index + match.length;
  }

  result += markdown.slice(lastIndex);
  return result;
}

export function renderActivityLogMarkdownToHtml(markdown: string, aiAgentLabel: string) {
  return parseActivityLogMarkdownBlocks(markdown, aiAgentLabel)
    .map((block) => {
      if (block.type === 'table') {
        const thead = `<thead><tr>${block.headers
          .map((cell, index) => `<th data-align="${block.alignments[index] ?? 'left'}">${cell}</th>`)
          .join('')}</tr></thead>`;
        const tbody =
          block.rows.length > 0
            ? `<tbody>${block.rows
                .map(
                  (cells) =>
                    `<tr>${cells
                      .map((cell, index) => `<td data-align="${block.alignments[index] ?? 'left'}">${cell}</td>`)
                      .join('')}</tr>`
                )
                .join('')}</tbody>`
            : '';
        return `<table>${thead}${tbody}</table>`;
      }

      return block.html;
    })
    .filter(Boolean)
    .join('');
}

export function parseActivityLogMarkdownBlocks(markdown: string, aiAgentLabel: string): ActivityLogMarkdownBlock[] {
  return parseActivityLogMarkdownBlocksWithInlineRenderer(markdown, (text) => renderInlineHtml(text, aiAgentLabel));
}
